import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { useSession } from "@/services/auth0/session";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Invoice } from "@/api.types";
import { logger } from "@/services/logger";

const statusColors = {
  DRAFT: "bg-gray-500",
  OPEN: "bg-blue-500",
  PAID: "bg-green-500",
  VOID: "bg-red-500",
};

export function CustomerInvoicesTable() {
  const { customer, token } = useSession();
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["invoices", customer?.id],
    queryFn: async () => {
      const response = await axios.get<{ data: Invoice[] }>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/invoices`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!customer?.id,
  });

  const invoices = data?.data ?? [];

  const filteredInvoices = useMemo(() => {
    if (!searchTerm) return invoices;

    return invoices.filter((invoice) => {
      const invoiceId = invoice?.stripeInvoiceData?.id || "";
      const orderNumbers =
        invoice?.invoiceOrders
          ?.map((io) => io.order?.orderNumber || "")
          .filter(Boolean)
          .join(" ") || "";

      return (
        invoiceId.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        orderNumbers.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [invoices, searchTerm]);

  const handleDownloadPDF = (invoice: Invoice) => {
    try {
      if (!invoice.stripeInvoiceData.pdfUrl) {
        logger.error("No PDF URL available for this invoice");
        return;
      }
      window.open(invoice.stripeInvoiceData.pdfUrl);
    } catch (error) {
      logger.error("Error opening invoice PDF:", error);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    try {
      if (!invoice.stripeInvoiceData.hostedUrl) {
        logger.error("No hosted URL available for this invoice");
        return;
      }
      window.open(invoice.stripeInvoiceData.hostedUrl);
    } catch (error) {
      logger.error("Error opening invoice:", error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex sm:items-center justify-between sm:flex-row flex-col gap-2">
        <Input
          placeholder="Search invoices..."
          className="sm:max-w-sm w-full"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice Number</TableHead>
              <TableHead className="hidden sm:table-cell">
                Service Date Range
              </TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead className="hidden sm:table-cell">Due Date</TableHead>
              <TableHead className="hidden sm:table-cell">Payment</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center">
                  No invoices found
                </TableCell>
              </TableRow>
            ) : (
              filteredInvoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">
                        #{invoice.stripeInvoiceData?.number || "Draft"}
                      </p>
                      <p className="text-sm text-gray-500 hidden sm:block">
                        Created:{" "}
                        {format(new Date(invoice.createdAt), "MMM d, yyyy")}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div>
                      {invoice.orderDateRange?.start
                        ? format(
                            parseISO(invoice.orderDateRange.start),
                            "MMM d, yyyy"
                          )
                        : "N/A"}{" "}
                      <span className="text-sm text-gray-500">{" - "}</span>{" "}
                      {invoice.orderDateRange?.end
                        ? format(
                            parseISO(invoice.orderDateRange.end),
                            "MMM d, yyyy"
                          )
                        : "N/A"}
                    </div>
                  </TableCell>
                  <TableCell>{invoice.totalOrders || 0}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        statusColors[
                          (invoice.stripeInvoiceData?.status.toUpperCase() as keyof typeof statusColors) ||
                            "NOT AVAILABLE"
                        ]
                      }
                      variant="default"
                    >
                      {invoice.stripeInvoiceData?.status.toUpperCase() ||
                        "NOT AVAILABLE"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      $
                      {((invoice.stripeInvoiceData?.amount || 0) / 100).toFixed(
                        2
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {invoice.stripeInvoiceData?.dueDate
                      ? format(
                          new Date(invoice.stripeInvoiceData.dueDate * 1000),
                          "MMM d, yyyy"
                        )
                      : "N/A"}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {!["PAID", "VOID"].includes(
                      invoice.stripeInvoiceData.status.toUpperCase()
                    ) &&
                      invoice.stripeInvoiceData.hostedUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewInvoice(invoice)}
                        >
                          Pay Now
                        </Button>
                      )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleDownloadPDF(invoice)}
                          disabled={!invoice.stripeInvoiceData.pdfUrl}
                        >
                          Download PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleViewInvoice(invoice)}
                          disabled={!invoice.stripeInvoiceData.hostedUrl}
                        >
                          View Invoice
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
